/* eslint-disable max-lines */
import maplibregl, {
  type FillLayerSpecification,
  type GeoJSONSourceSpecification,
  type LineLayerSpecification,
  type RasterLayerSpecification,
  type RasterSourceSpecification,
  type SymbolLayerSpecification,
  type VectorSourceSpecification
} from 'maplibre-gl'
import { MAP_SOURCE_TYPE } from '@/constants/lib/maplibreConsts'
import { ZOOM_THRESHOLD } from '@/constants/ui/map/mapConstants'
import { useDisasterStore } from '@/stores/disasterStore'
import { STATUS_ICON_MAP } from '@/constants/ui/map/mapConstants'
import type { MapLayer } from '@/types/ui/map/mapTypes'

export const useMapLayers = () => {
  const disasterStore = useDisasterStore()

  const GOV_AND_DISASTER = `${disasterStore.govCode}_${disasterStore.disasterId}`
  const GRID_SOURCE_LAYER = `grid_${GOV_AND_DISASTER}_layer`
  const POINT_SOURCE_LAYER = `point_${GOV_AND_DISASTER}_layer`
  const GRID_LABEL_MIN_ZOOM = 12

  /**
   * GSIP用ソース
   * @param layerInfo MapLayer
   * @returns GSIP用ソース
   */
  const getGSIPSource = (layerInfo: MapLayer) => {
    return {
      type: MAP_SOURCE_TYPE.RASTER,
      tiles: [`gsip://${layerInfo.key}/{z}/{x}/{y}`], // カスタムスキーム利用
      tileSize: 256,
      attribution: ''
    }
  }

  /**
   * GSIP用レイヤー
   * @param layerInfo MapLayer
   * @returns GSIP用レイヤー
   */
  const getGSIPLayer = (layerInfo: MapLayer) => {
    return {
      id: layerInfo.layerId,
      type: MAP_SOURCE_TYPE.RASTER,
      source: layerInfo.sourceId,
      minzoom: 0,
      maxzoom: ZOOM_THRESHOLD.MAX_LAYER,
      paint: { 'raster-opacity': layerInfo.key === 'baseMap' ? 1 : 0.5 }
    }
  }

  /** ソース定義 ヒートマップ */
  const GRID_SOURCE_ID = 'grid_source'
  const GRID_SOURCE: VectorSourceSpecification = {
    type: MAP_SOURCE_TYPE.VECTOR,
    tiles: ['gsp://localhost/?layer=grid&bbox={bbox-epsg-3857}'],
    scheme: 'xyz'
  }

  /** ソース定義 点群 */
  const POINT_SOURCE_ID = 'point_source'
  const POINT_SOURCE: VectorSourceSpecification = {
    type: MAP_SOURCE_TYPE.VECTOR,
    tiles: ['gsp://localhost/?layer=point&bbox={bbox-epsg-3857}'],
    scheme: 'xyz'
  }

  /** ソース定義 オルソ画像 */
  const ORTHO_SOURCE_ID = 'ortho_source'
  const ORTHO_SOURCE: RasterSourceSpecification = {
    type: MAP_SOURCE_TYPE.RASTER,
    tiles: ['gsp://localhost/?layer=ortho&bbox={bbox-epsg-3857}&z={z}&x={x}&y={y}'],
    tileSize: 256,
    attribution: ''
  }

  /** ソース定義 航空写真 */
  const AERIAL_SOURCE_ID = 'aerial_source'
  const AERIAL_SOURCE: RasterSourceSpecification = {
    type: MAP_SOURCE_TYPE.RASTER,
    tiles: ['gsp://localhost/?layer=aerial&bbox={bbox-epsg-3857}&z={z}&x={x}&y={y}'],
    tileSize: 256,
    attribution: ''
  }

  /** ソース定義 空撮範囲 */
  const AERIALAREAS_SOURCE_ID = 'aerialareas_source'
  const AERIALAREAS_SOURCE: GeoJSONSourceSpecification = {
    type: MAP_SOURCE_TYPE.GEOJSON,
    data: 'aap://localhost/?layer=aerialAreas'
  }
  /** ソース定義 ハザードマップ(急傾斜地の崩壊) */
  const SLOPE_FAILURE_SOURCE: RasterSourceSpecification = {
    type: MAP_SOURCE_TYPE.RASTER,
    tiles: ['gsip://slopeFailure/{z}/{x}/{y}'],
    tileSize: 256,
    attribution: '',
    maxzoom: ZOOM_THRESHOLD.MAX_HAZARD_LAYER
  }
  /** ソース定義 ハザードマップ(土石流) */
  const DEBRIS_FLOW_SOURCE: RasterSourceSpecification = {
    type: MAP_SOURCE_TYPE.RASTER,
    tiles: ['gsip://debrisFlow/{z}/{x}/{y}'],
    tileSize: 256,
    attribution: '',
    maxzoom: ZOOM_THRESHOLD.MAX_HAZARD_LAYER
  }
  /** ソース定義 ハザードマップ (地すべり)*/
  const LAND_SLIDE_SOURCE: RasterSourceSpecification = {
    type: MAP_SOURCE_TYPE.RASTER,
    tiles: ['gsip://landslide/{z}/{x}/{y}'],
    tileSize: 256,
    attribution: '',
    maxzoom: ZOOM_THRESHOLD.MAX_HAZARD_LAYER
  }
  /** ソース定義 ハザードマップ (想定最大規模)*/
  const FLOOD_MAX_SCALE_SOURCE: RasterSourceSpecification = {
    type: MAP_SOURCE_TYPE.RASTER,
    tiles: ['gsip://floodMaxScale/{z}/{x}/{y}'],
    tileSize: 256,
    attribution: '',
    maxzoom: ZOOM_THRESHOLD.MAX_HAZARD_LAYER
  }
  /** ソース定義 ハザードマップ (計画規模)*/
  const FLOOD_PLANNED_SCALE_SOURCE: RasterSourceSpecification = {
    type: MAP_SOURCE_TYPE.RASTER,
    tiles: ['gsip://floodPlannedScale/{z}/{x}/{y}'],
    tileSize: 256,
    attribution: '',
    maxzoom: ZOOM_THRESHOLD.MAX_HAZARD_LAYER
  }
  /** ソース定義 ハザードマップ (津波浸水想定区域)*/
  const TSUNAMI_INUNDATION_SOURCE: RasterSourceSpecification = {
    type: MAP_SOURCE_TYPE.RASTER,
    tiles: ['gsip://tsunamiInundation/{z}/{x}/{y}'],
    tileSize: 256,
    attribution: '',
    maxzoom: ZOOM_THRESHOLD.MAX_HAZARD_LAYER
  }
  /** レイヤー定義 ヒートマップグリッド */
  const GRID_FILL_LAYER: FillLayerSpecification = {
    id: 'grid_fill_layer',
    type: MAP_SOURCE_TYPE.FILL,
    source: GRID_SOURCE_ID,
    'source-layer': GRID_SOURCE_LAYER,
    minzoom: 0,
    maxzoom: ZOOM_THRESHOLD.NARROW,
    paint: {
      'fill-color': ['interpolate', ['linear'], ['get', 'density'], 0, '#00ff00', 1, '#ff0000'],
      'fill-opacity': ['case', ['==', ['get', 'total_flags'], 0], 0, 0.4]
    }
  }

  /** レイヤー定義 ヒートマップラベル */
  const GRID_LABEL_WIDE_LAYER: SymbolLayerSpecification = {
    id: 'grid_label_wide_layer',
    type: MAP_SOURCE_TYPE.SYMBOL,
    source: GRID_SOURCE_ID,
    'source-layer': GRID_SOURCE_LAYER,
    minzoom: GRID_LABEL_MIN_ZOOM,
    maxzoom: ZOOM_THRESHOLD.MIDDLE,
    layout: {
      'text-anchor': 'center',
      'text-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        GRID_LABEL_MIN_ZOOM,
        6,
        ZOOM_THRESHOLD.MIDDLE,
        28
      ],
      'text-field': [
        'case',
        ['==', ['get', 'total_flags'], 0],
        '',
        ['format', ['get', 'total_flags'], { 'font-scale': 1.2 }]
      ]
    },
    paint: {
      'text-color': '#ffffff',
      'text-halo-color': '#000000',
      'text-halo-width': 1,
      'text-halo-blur': 0
    }
  }
  const GRID_LABEL_MIDDLE_LAYER: SymbolLayerSpecification = {
    ...GRID_LABEL_WIDE_LAYER,
    id: 'grid_label_middle_layer',
    minzoom: ZOOM_THRESHOLD.MIDDLE,
    maxzoom: ZOOM_THRESHOLD.NARROW,
    layout: {
      ...GRID_LABEL_WIDE_LAYER.layout,
      'text-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        ZOOM_THRESHOLD.MIDDLE,
        6,
        ZOOM_THRESHOLD.NARROW,
        28
      ]
    }
  }

  /** レイヤー定義 オルソ画像 */
  const ORTHO_LAYER: RasterLayerSpecification = {
    id: 'ortho_layer',
    type: MAP_SOURCE_TYPE.RASTER,
    source: ORTHO_SOURCE_ID,
    minzoom: 0,
    maxzoom: ZOOM_THRESHOLD.MAX_LAYER
  }

  /** レイヤー定義 航空写真 */
  const AERIAL_LAYER: RasterLayerSpecification = {
    id: 'aerial_layer',
    type: MAP_SOURCE_TYPE.RASTER,
    source: AERIAL_SOURCE_ID,
    minzoom: 0,
    maxzoom: ZOOM_THRESHOLD.MAX_LAYER
  }

  /** レイヤー定義 空撮範囲（塗りつぶし） */
  const AERIALAREAS_FILL_LAYER: FillLayerSpecification = {
    id: 'aerialareas_fill_layer',
    type: MAP_SOURCE_TYPE.FILL,
    source: AERIALAREAS_SOURCE_ID,
    minzoom: 0,
    maxzoom: ZOOM_THRESHOLD.NARROW,
    paint: {
      'fill-color': '#ADFF2F',
      'fill-opacity': 0.4
    }
  }

  /** レイヤー定義 空撮範囲（枠線） */
  const AERIALAREAS_LINE_LAYER: LineLayerSpecification = {
    id: 'aerialareas_line_layer',
    type: MAP_SOURCE_TYPE.LINE,
    source: AERIALAREAS_SOURCE_ID,
    minzoom: 0,
    maxzoom: ZOOM_THRESHOLD.NARROW,
    paint: {
      'line-color': '#000000ff',
      'line-width': 1
    }
  }

  /**
   * MapLibreのicon-imageプロパティに対応する配列を作成する
   * @param なし
   * @returns icon-imageプロパティに対応する配列
   */
  const createIconImageMatchExpression =
    (): maplibregl.DataDrivenPropertyValueSpecification<string> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const expression: any[] = ['match', ['get', 'status_code']]
      for (const [status, iconName] of Object.entries(STATUS_ICON_MAP)) {
        expression.push(status, iconName)
      }
      expression.push('icon-default') // デフォルトアイコン
      return expression as unknown as maplibregl.DataDrivenPropertyValueSpecification<string>
    }

  /** レイヤー定義 点群 */
  const POINT_LAYER: SymbolLayerSpecification = {
    id: 'point_layer',
    type: MAP_SOURCE_TYPE.SYMBOL,
    source: POINT_SOURCE_ID,
    'source-layer': POINT_SOURCE_LAYER,
    minzoom: ZOOM_THRESHOLD.NARROW,
    maxzoom: ZOOM_THRESHOLD.MAX_LAYER,
    layout: {
      'icon-image': createIconImageMatchExpression(),
      'icon-size': 0.5,
      'icon-allow-overlap': true,
      'icon-anchor': 'center',
      'text-offset': [0, 1.2],
      'text-anchor': 'top',
      'text-optional': true
    }
  }

  /** 重ね合わせるレイヤ定義
   * ベースマップ（地理院地図）
   *   ∟航空写真
   *     ∟オルソ画像
   *       ∟急傾斜地崩落警戒区域（透過あり）
   *         ∟土石流警戒区域（透過あり）
   *           ∟洪水浸水想定区域（透過あり）
   *             ∟津波浸水想定区域（透過あり）
   *               ∟ヒートマップ（透過あり）・点群
   *                 ∟ドローン空撮範囲
   * ※表示状態は mapViewSettingsStore.ts で保持
   */
  const MAP_LAYERS: MapLayer[] = [
    // ベースマップ
    { key: 'baseMap', layerId: 'layer-baseMap', sourceId: 'source-baseMap' },
    // 航空写真
    {
      key: 'aerialPhoto',
      layerId: AERIAL_LAYER.id,
      sourceId: AERIAL_SOURCE_ID,
      source: AERIAL_SOURCE,
      layer: AERIAL_LAYER
    },
    // オルソ画像
    {
      key: 'orthoPhoto',
      layerId: ORTHO_LAYER.id,
      sourceId: ORTHO_SOURCE_ID,
      source: ORTHO_SOURCE,
      layer: ORTHO_LAYER
    },
    // 土砂災害警戒区域（急傾斜地の崩壊）
    {
      key: 'slopeFailure',
      layerId: 'layer-slopeFailure',
      sourceId: 'source-slopeFailure',
      source: SLOPE_FAILURE_SOURCE
    },
    // 土砂災害警戒区域（土石流）
    {
      key: 'debrisFlow',
      layerId: 'layer-debrisFlow',
      sourceId: 'source-debrisFlow',
      source: DEBRIS_FLOW_SOURCE
    },
    // 土砂災害警戒区域（地すべり）
    {
      key: 'landslide',
      layerId: 'layer-landslide',
      sourceId: 'source-landslide',
      source: LAND_SLIDE_SOURCE
    },
    // 洪水浸水想定区域（想定最大規模）
    {
      key: 'floodMaxScale',
      layerId: 'layer-floodMaxScale',
      sourceId: 'source-floodMaxScale',
      source: FLOOD_MAX_SCALE_SOURCE
    },
    // 洪水浸水想定区域（計画規模）
    {
      key: 'floodPlannedScale',
      layerId: 'layer-floodPlannedScale',
      sourceId: 'source-floodPlannedScale',
      source: FLOOD_PLANNED_SCALE_SOURCE
    },
    // 津波浸水想定区域
    {
      key: 'tsunamiInundation',
      layerId: 'layer-tsunamiInundation',
      sourceId: 'source-tsunamiInundation',
      source: TSUNAMI_INUNDATION_SOURCE
    },
    // ヒートマップ（透過あり）
    {
      key: 'gridFill',
      layerId: GRID_FILL_LAYER.id,
      sourceId: GRID_SOURCE_ID,
      source: GRID_SOURCE,
      layer: GRID_FILL_LAYER
    },
    {
      key: 'gridLabelWide',
      layerId: GRID_LABEL_WIDE_LAYER.id,
      sourceId: GRID_SOURCE_ID,
      source: GRID_SOURCE,
      layer: GRID_LABEL_WIDE_LAYER
    },
    {
      key: 'gridLabelMiddle',
      layerId: GRID_LABEL_MIDDLE_LAYER.id,
      sourceId: GRID_SOURCE_ID,
      source: GRID_SOURCE,
      layer: GRID_LABEL_MIDDLE_LAYER
    },
    // 建物マーカー
    {
      key: 'point',
      layerId: POINT_LAYER.id,
      sourceId: POINT_SOURCE_ID,
      source: POINT_SOURCE,
      layer: POINT_LAYER
    },
    // ドローン空撮範囲
    {
      key: 'aerialAreasFill',
      layerId: AERIALAREAS_FILL_LAYER.id,
      sourceId: AERIALAREAS_SOURCE_ID,
      source: AERIALAREAS_SOURCE,
      layer: AERIALAREAS_FILL_LAYER
    },
    {
      key: 'aerialAreasLine',
      layerId: AERIALAREAS_LINE_LAYER.id,
      sourceId: AERIALAREAS_SOURCE_ID,
      source: AERIALAREAS_SOURCE,
      layer: AERIALAREAS_LINE_LAYER
    }
  ]
  return {
    MAP_LAYERS,
    GRID_SOURCE_ID,
    POINT_SOURCE_ID,
    AERIALAREAS_SOURCE_ID,
    POINT_LAYER,
    AERIALAREAS_FILL_LAYER,
    getGSIPSource,
    getGSIPLayer
  }
}
